<template>
  <b-card 
    class="lg-status-card" 
    :class="[`lg-status-card--${variant}`, { 'lg-status-card--animated': animated }]"
    no-body
  >
    <div class="lg-status-card__content">
      <div class="lg-status-card__icon">
        <b-icon :icon="icon" :font-scale="iconScale" />
      </div>
      <div class="lg-status-card__info">
        <div
          ref="valueEl"
          :id="valueId"
          class="lg-status-card__value"
          :class="valueSizeClass"
          :key="value"
        >
          {{ displayValue }}
        </div>
        <b-tooltip :target="valueId" triggers="hover focus" container="body">
          {{ displayValue }}
        </b-tooltip>
        <div class="lg-status-card__label">{{ label }}</div>
        <div class="lg-status-card__sublabel" v-if="sublabel">{{ sublabel }}</div>
      </div>
    </div>
  </b-card>
</template>

<script>
export default {
  name: 'LgStatusCard',
  props: {
    value: {
      type: [Number, String],
      required: true
    },
    label: {
      type: String,
      required: true
    },
    sublabel: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'success',
      validator: (value) => ['success', 'danger', 'warning', 'info', 'primary'].includes(value)
    },
    icon: {
      type: String,
      default: 'check-circle'
    },
    iconScale: {
      type: [Number, String],
      default: 1.5
    },
    animated: {
      type: Boolean,
      default: true
    },
    formatValue: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      isOverflowing: false,
    };
  },
  computed: {
    displayValue() {
      if (this.formatValue) {
        return this.formatValue(this.value);
      }
      return this.value;
    },
    valueSizeClass() {
      const raw = String(this.displayValue || '').replace(/[^0-9]/g, '');
      const len = raw.length;
      if (len > 11) return 'lg-status-card__value--xs';
      if (len > 8) return 'lg-status-card__value--sm';
      return '';
    },
    valueId() {
      return `lg-status-val-${this._uid}`;
    },
  }
  ,
  mounted() {
    this.recalcOverflow();
  },
  updated() {
    this.recalcOverflow();
  },
  methods: {
    recalcOverflow() {
      this.$nextTick(() => {
        const el = this.$refs.valueEl;
        if (!el) return;
        try {
          this.isOverflowing = el.scrollWidth > el.clientWidth;
        } catch (e) {
          this.isOverflowing = false;
        }
      });
    },
  }
};
</script>

<style scoped lang="scss">
.lg-status-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--status-card-accent);
    opacity: 0.8;
  }

  &__content {
    display: flex;
    align-items: center;
    padding: 1.25rem 1.5rem;
    gap: 1rem;
  }

  &__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: var(--status-card-icon-bg);
    color: var(--status-card-icon-color);
    transition: all 0.3s ease;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__value {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.2;
    color: var(--status-card-value-color);
    margin-bottom: 0.25rem;
    transition: all 0.3s ease;
    cursor: help;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    
    .lg-status-card--animated & {
      animation: pulse 0.6s ease-out;
    }
  }

  &__value--sm {
    font-size: 1.35rem;
  }

  &__value--xs {
    font-size: 1.15rem;
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--status-card-label-color);
    margin-bottom: 0.125rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__sublabel {
    font-size: 0.75rem;
    color: var(--status-card-sublabel-color);
    opacity: 0.8;
  }

  // Variantes de cor usando variáveis CSS
  &--success {
    --status-card-accent: var(--color-success, #2EBD85);
    --status-card-icon-bg: rgba(46, 189, 133, 0.15);
    --status-card-icon-color: var(--color-success, #2EBD85);
    --status-card-value-color: var(--color-success, #2EBD85);
    --status-card-label-color: var(--color-text, #263040);
    --status-card-sublabel-color: var(--color-text, #263040);
    background: linear-gradient(135deg, rgba(46, 189, 133, 0.05) 0%, rgba(46, 189, 133, 0.02) 100%);
    color: var(--color-text, #263040);
  }

  &--danger {
    --status-card-accent: var(--color-danger, #e74c3c);
    --status-card-icon-bg: rgba(231, 76, 60, 0.15);
    --status-card-icon-color: var(--color-danger, #e74c3c);
    --status-card-value-color: var(--color-danger, #e74c3c);
    --status-card-label-color: var(--color-text, #263040);
    --status-card-sublabel-color: var(--color-text, #263040);
    background: linear-gradient(135deg, rgba(231, 76, 60, 0.05) 0%, rgba(231, 76, 60, 0.02) 100%);
    color: var(--color-text, #263040);
  }

  &--warning {
    --status-card-accent: var(--color-warning, #F5A623);
    --status-card-icon-bg: rgba(245, 166, 35, 0.15);
    --status-card-icon-color: var(--color-warning, #F5A623);
    --status-card-value-color: var(--color-warning, #F5A623);
    --status-card-label-color: var(--color-text, #263040);
    --status-card-sublabel-color: var(--color-text, #263040);
    background: linear-gradient(135deg, rgba(245, 166, 35, 0.05) 0%, rgba(245, 166, 35, 0.02) 100%);
    color: var(--color-text, #263040);
  }

  &--info {
    --status-card-accent: var(--color-info, #45b8e6);
    --status-card-icon-bg: rgba(69, 184, 230, 0.15);
    --status-card-icon-color: var(--color-info, #45b8e6);
    --status-card-value-color: var(--color-info, #45b8e6);
    --status-card-label-color: var(--color-text, #263040);
    --status-card-sublabel-color: var(--color-text, #263040);
    background: linear-gradient(135deg, rgba(69, 184, 230, 0.05) 0%, rgba(69, 184, 230, 0.02) 100%);
    color: var(--color-text, #263040);
  }

  &--primary {
    --status-card-accent: var(--color-primary, #24528D);
    --status-card-icon-bg: rgba(36, 82, 141, 0.15);
    --status-card-icon-color: var(--color-primary, #24528D);
    --status-card-value-color: var(--color-primary, #24528D);
    --status-card-label-color: var(--color-text, #263040);
    --status-card-sublabel-color: var(--color-text, #263040);
    background: linear-gradient(135deg, rgba(36, 82, 141, 0.05) 0%, rgba(36, 82, 141, 0.02) 100%);
    color: var(--color-text, #263040);
  }
}

// Dark mode support
body.dark-mode {
  .lg-status-card {
    background: var(--color-card-bg, #21262f);
    
    &__icon {
      background: var(--status-card-icon-bg);
    }
    
    &__label,
    &__sublabel {
      color: var(--color-card-text, #f6f9fc);
    }
  }
}

// Animação sutil para mudança de valores
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

// Responsividade
@media (max-width: 576px) {
  .lg-status-card {
    &__content {
      padding: 1rem;
      gap: 0.75rem;
    }

    &__icon {
      width: 48px;
      height: 48px;
    }

    &__value {
      font-size: 1.75rem;
    }

    &__label {
      font-size: 0.8125rem;
    }
  }
}

</style>

